<template>
  <div class="w-full h-full">
    <Card>
      <CardHeader>
        <CardTitle>Transaction processing</CardTitle>
        <CardDescription
          >View the transactions processed by the Transaction Enrichment API</CardDescription
        >
        <CardAction class="flex items-center gap-2">
          <label class="flex items-center gap-2 text-sm text-muted-foreground">
            <span class="hidden sm:inline">Type</span>
            <select
              v-model="transactionType"
              class="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
              @change="onTypeFilterChange"
            >
              <option value="ALL">All types</option>
              <option value="OB">Open banking</option>
              <option value="CARD">Card</option>
            </select>
          </label>
          <Button size="sm" @click="$router.push('/transaction-processing/uploads')">
            <Upload class="w-4 h-4" />Upload transactions
          </Button>
          <Button
            v-if="showDeleteTransactionButton"
            size="sm"
            variant="destructive"
            @click="deleteTransactions()"
          >
            <Trash2 class="w-4 h-4" />Delete all
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="transactions"
          :loading="loading"
          :error="error"
          @retry="getTransactions(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by transaction id or user id"
          :search="search"
          @update:search="search = $event"
          @search="onSearch"
          @clear-search="onClearSearch"
        >
          <template #item-id="transaction">
            <CopyId :value="transaction.id" :max-width="120" />
          </template>
          <template #item-bookingDateTime="transaction">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{
              niceDateTime(transaction.bookingDateTime)
            }}</span>
          </template>
          <template #item-amount="transaction">
            <span class="text-sm tabular-nums whitespace-nowrap">
              {{ transaction.transactionAmount.currency
              }}{{ (Math.round(transaction.transactionAmount.amount * 100) / 100).toFixed(2) }}
            </span>
          </template>
          <template #item-customerTransactionId="transaction">
            <CopyId :value="transaction.internalTransactionId" :max-width="120" />
          </template>
          <template #item-remittanceInformationUnstructured="transaction">
            <div class="w-64">
              <p class="text-xs text-muted-foreground">
                {{ transaction.remittanceInformationUnstructured }}
              </p>
            </div>
          </template>
          <template #item-internalEndUserId="transaction">
            <router-link
              v-if="transaction.endUserId"
              :to="`/enrolled-users/users/${transaction.endUserId}`"
              class="text-xs font-mono font-medium whitespace-nowrap text-sky-600 dark:text-sky-400 hover:text-sky-500 dark:hover:text-sky-300 hover:underline underline-offset-2 transition-colors"
              @click.stop
            >
              {{ transaction.endUserId }}
            </router-link>
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-action="transaction">
            <div class="flex justify-end items-center gap-1">
              <Button
                v-if="showDeleteTransactionButton"
                size="sm"
                variant="destructive"
                @click="deleteTransactionFromId(transaction.id)"
                >Delete</Button
              >
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  CopyId,
} from '@boogi/ui'
import { Upload, Trash2 } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'

export default {
  name: 'transaction-enrichment',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    CopyId,
    Upload,
    Trash2,
  },
  props: {},

  data() {
    return {
      showDeleteTransactionButton: localStorage.getItem('apiUrl').includes('sandbox'),

      serverOptions: {
        page: 1,
        rowsPerPage: 50,
      },
      serverItemsLength: undefined,

      search: '',
      type: 'all',
      transactionType: 'ALL',
      searchDebounce: null,

      loading: true,
      error: false,
      headersTable1: [
        { text: 'Boogi id', value: 'id' },
        { text: 'Transaction id', value: 'customerTransactionId' },
        { text: 'Transaction type', value: 'transactionType' },

        { text: 'End user id', value: 'internalEndUserId' },
        { text: 'Booking DateTime', value: 'bookingDateTime' },
        { text: 'Amount', value: 'amount' },
        { text: 'Input description', value: 'remittanceInformationUnstructured' },
        { text: 'Action', value: 'action' },
      ],
      transactions: [],
    }
  },
  created() {
    this.getTransactions(0, 50)
  },
  methods: {
    onSearch() {
      const term = this.search.trim()
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 50 }
      if (term) {
        this.getTransactionForAnEndUser(0, this.serverOptions.rowsPerPage, term)
      } else {
        this.getTransactions(0, this.serverOptions.rowsPerPage)
      }
    },
    onClearSearch() {
      this.search = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 50 }
      this.getTransactions(0, this.serverOptions.rowsPerPage)
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toUTC().toLocaleString(DateTime.DATETIME_MED)
    },

    // Type filter (All / Open banking / Card) applies to the full processing list. Changing it
    // returns to the list view and resets to the first page.
    onTypeFilterChange() {
      this.search = ''
      this.type = 'all'
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 50 }
    },
    async getTransactionForAnEndUser(page, size, search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.type = 'endUser'

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment/user/${search}?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          // No transactions for that user id — treat the term as a transaction id.
          if (!object.content || object.content.length === 0) {
            await this.getTransactionFromId(search)
            return
          }
          this.transactions = object.content
          this.serverItemsLength = object.totalElements
        } else {
          // Not a valid user id — fall back to a transaction-id lookup.
          await this.getTransactionFromId(search)
          return
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getTransactionFromId(search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment/transaction/${search}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          var array = []
          array.push(object)

          this.transactions = array
          this.serverItemsLength = 1
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async deleteTransactionFromId(search) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        this.transactions = []

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment/transaction/${search}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getTransactions(0, 25)
          this.loading = false
        }
      } catch (error) {
        return
      }
    },

    async deleteTransactions() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        this.transactions = []

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment/transaction/deleteAll`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getTransactions(0, 25)
          this.loading = false
        }
      } catch (error) {
        return
      }
    },

    async getTransactions(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.type = 'all'

      this.loading = true
      this.error = false

      try {
        const typeParam =
          this.transactionType && this.transactionType !== 'ALL'
            ? `&transactionType=${this.transactionType}`
            : ''
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment?page=${page}&size=${size}${typeParam}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.transactions = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {},
  mounted() {
    document.title = 'Dashboard | Transaction enrichment'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        switch (this.type) {
          case 'all':
            this.getTransactions(val.page - 1, val.rowsPerPage)
            break
          case 'endUser':
            this.getTransactionForAnEndUser(val.page - 1, val.rowsPerPage, this.search)
            break
        }
      },
    },
  },
}
</script>
